import { useEffect, useState, useMemo } from "react";
import type {
  Type_availableShifts,
  availableShifts as AvailableShifts,
} from "@vTypes";
import { getDynamicFontSize } from "@utils/fontSizeUtils";
import { CircleArrowLeft, CircleArrowRight } from "lucide-react";
import { cn } from "@/utils/cn";
import { getDefaultShiftsSortedBySequence } from "@/utils/shift-util";
import useRosterConfig from "@/hooks/useRosterConfig";
import CustomDragNDropModal from "@/components/modal/CustomDragNDropModal";

/** 동일 shift_id가 여러 행일 때 선택·하이라이트는 DB id로 구분 */
function isSameShiftRow(
  selected: Type_availableShifts | null,
  shift: Type_availableShifts
): boolean {
  if (!selected) return false;
  const a = selected as Type_availableShifts & { id?: number };
  const b = shift as Type_availableShifts & { id?: number };
  if (
    a.id != null &&
    b.id != null &&
    Number.isFinite(Number(a.id)) &&
    Number.isFinite(Number(b.id))
  ) {
    return Number(a.id) === Number(b.id);
  }
  return selected.shift_id === shift.shift_id;
}

function shiftRowKey(
  shift: Type_availableShifts & { id?: number },
  index: number
) {
  if (shift.id != null && Number.isFinite(Number(shift.id))) {
    return `shift-row-${shift.id}`;
  }
  return `shift-row-${shift.shift_id}-${index}`;
}

function ModalRosterModify({
  modalClose,
  availableShifts,
  selectedShift,
  setSelectedShift,
  deleteAllShift,
  onUndo,
  onRedo,
  title,
  deleteBtnVersion,
  canUndo,
  canRedo,
  portalZIndex = 10,
  targetRef,
}: {
  modalClose: (value: boolean) => void;
  availableShifts: AvailableShifts[] | Type_availableShifts[];
  selectedShift: Type_availableShifts | null;
  setSelectedShift: (value: Type_availableShifts | null) => void;
  deleteAllShift: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  title?: string;
  deleteBtnVersion?: boolean;
  canUndo?: boolean;
  canRedo?: boolean;
  portalZIndex?: number;
  targetRef?: React.RefObject<HTMLElement | null>;
}) {
  const [defaultShifts, setDefaultShifts] = useState<Type_availableShifts[]>(
    []
  );
  const [customShifts, setCustomShifts] = useState<Type_availableShifts[]>([]);

  const { data: serverConfig } = useRosterConfig();
  const isUseMid = serverConfig?.use_mid;

  type ShiftWithDefault = Type_availableShifts & {
    default_shift?: string | null;
    sequence?: number;
  };

  // isUseMid false면 M을 아예 제외한 목록만 사용 (default/custom 어디에도 안 나오게)
  // "주"는 배치로 돌아가기 때문에 직접 수정하면 안 되므로 필터링
  const shiftsForModal = useMemo(() => {
    if (!availableShifts?.length) return [];
    let filtered = availableShifts;

    // "주" 근무코드 필터링 (배치로 돌아가기 때문에 직접 수정 불가)
    filtered = filtered.filter((s) => s.shift_id !== "주");

    // isUseMid false면 M도 제외
    if (isUseMid === false) {
      filtered = (filtered as ShiftWithDefault[]).filter(
        (s) => s.default_shift !== "M"
      ) as Type_availableShifts[];
    }

    return filtered;
  }, [availableShifts, isUseMid]);

  // default_shift 기준 메인 시프트 (shiftsForModal 이미 M 제외됨)
  const defaultShiftsFromData = useMemo(() => {
    if (!shiftsForModal.length) return [];
    return getDefaultShiftsSortedBySequence(
      shiftsForModal as ShiftWithDefault[],
      { excludeOff: false }
    ) as Type_availableShifts[];
  }, [shiftsForModal]);

  const customShiftsFromData = useMemo(() => {
    if (!shiftsForModal.length) return [];
    const defaultKeys = new Set(
      defaultShiftsFromData.map((s) => {
        const sid = (s as Type_availableShifts & { id?: number }).id;
        return sid != null && Number.isFinite(Number(sid))
          ? `id:${sid}`
          : `sid:${s.shift_id}:${(s as ShiftWithDefault).sequence ?? 0}`;
      })
    );
    return shiftsForModal.filter((s) => {
      const sid = (s as Type_availableShifts & { id?: number }).id;
      const key =
        sid != null && Number.isFinite(Number(sid))
          ? `id:${sid}`
          : `sid:${s.shift_id}:${(s as ShiftWithDefault).sequence ?? 0}`;
      return !defaultKeys.has(key);
    });
  }, [shiftsForModal, defaultShiftsFromData]);

  useEffect(() => {
    setDefaultShifts(defaultShiftsFromData);
    setCustomShifts(customShiftsFromData);
  }, [defaultShiftsFromData, customShiftsFromData]);

  const stableInitialPosition = useMemo(
    () => (targetRef ? undefined : { x: "50%", y: "50%" }),
    [targetRef]
  );

  return (
    <CustomDragNDropModal
      portalZIndex={portalZIndex}
      initialPosition={stableInitialPosition}
      targetRef={targetRef}
    >
      {({ onMouseDown }) => (
        <section
          className="modal_window modal_roster_modify pointer-events-auto bg-white shadow-[2px_2px_4px_2px_rgba(0,0,0,0.2)]"
          style={{
            minWidth: "600px",
            maxWidth: "100%",
            maxHeight: "100%",
            boxSizing: "border-box",
            position: "static",
            top: "auto",
            left: "auto",
            transform: "none",
          }}
        >
          <header
            className="modal_header gridBox modal_moveHandler !bg-[#f0f0f2]"
            onMouseDown={onMouseDown}
          >
            <div className="gridItem--left"></div>

            <div className="flex items-center p-3 ">
              {(onUndo || onRedo) && (
                <div className="flex left-0 absolute ml-4">
                  {onUndo && (
                    <div
                      className={cn(
                        "mr-2",
                        canUndo
                          ? "cursor-pointer"
                          : "cursor-not-allowed opacity-50"
                      )}
                      onClick={canUndo ? onUndo : undefined}
                    >
                      <CircleArrowLeft
                        size={26}
                        strokeWidth={1}
                        className="text-[#666666]"
                      />
                    </div>
                  )}
                  {onRedo && (
                    <div
                      className={cn(
                        canRedo
                          ? "cursor-pointer"
                          : "cursor-not-allowed opacity-50"
                      )}
                      onClick={canRedo ? onRedo : undefined}
                    >
                      <CircleArrowRight
                        size={26}
                        strokeWidth={1}
                        className="text-[#666666]"
                      />
                    </div>
                  )}
                </div>
              )}

              <h3 className="modal_title !text-[#333333] absolute left-1/2 -translate-x-1/2">
                {title || "근무표 수정하기"}
              </h3>
            </div>
            <div className="gridItem--right"></div>
            <button
              className="modal_button_close !top-[17px]"
              type="button"
              onClick={() => {
                modalClose(false);
                setSelectedShift(null);
              }}
            ></button>
          </header>

          <div className=" flex items-center justify-center pt-6 pb-3">
            <div className="mb-0">
              <p className="font-pretendard ">
                근무 코드를 선택 후, 입력하고 싶은 날짜를 선택하세요.
              </p>
            </div>
          </div>
          <div className="modal_body !px-[50px] ">
            <div className="shiftCode_container">
              {defaultShifts.map((shift, index) => {
                const isSelected = isSameShiftRow(selectedShift, shift);
                return (
                  <button
                    type="button"
                    key={shiftRowKey(
                      shift as Type_availableShifts & { id?: number },
                      index
                    )}
                    className={`button_inputShiftCode p-1 ${isSelected ? "selected" : ""}`}
                    title={shift.name}
                    style={{ backgroundColor: shift.color }}
                    onClick={() => {
                      setSelectedShift(isSelected ? null : shift);
                    }}
                  >
                    <span className="font-pretendard font-bold text-lg  truncate">
                      {shift.shift_id}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="shiftCode_container">
              {/* default_shift 메인 제외한 나머지 근무코드 */}
              {customShifts.map((shift, index) => {
                const isSelected = isSameShiftRow(selectedShift, shift);
                return (
                  <button
                    key={shiftRowKey(
                      shift as Type_availableShifts & { id?: number },
                      index
                    )}
                    type="button"
                    className={`button_inputShiftCode  ${isSelected ? "selected" : ""}`}
                    title={shift.name}
                    style={{ backgroundColor: shift.color }}
                    onClick={() => {
                      setSelectedShift(isSelected ? null : shift);
                    }}
                  >
                    <span
                      className="font-pretendard font-bold  whitespace-nowrap "
                      style={{ fontSize: getDynamicFontSize(shift.shift_id) }}
                    >
                      <span className="min-w-0 truncate text-lg">
                        {shift.shift_id}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </CustomDragNDropModal>
  );
}

export default ModalRosterModify;
